/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../Hooks/useAuth';
import TbhProfileHeader from './components/TbhProfileHeader';
import normalizeTableUtilityResponse from '../../Utils/tableUtilityResponse';
import './TbhResourceManagement.scss';
import { fetchBaseURL } from '../../Api/request/tbhResourceManagement';

const baseURL = fetchBaseURL();

const getSelectedTbhIdFromFilters = (filters = []) => {
  const tbhFilter = filters.find((filterItem) => filterItem?.field === 'tbh_id');

  return String(tbhFilter?.selectedValue || tbhFilter?.selectedValues?.[0] || '');
};

const TbhResourceManagement = () => {
  const { userProfile, authToken } = useAuth();
  const [tbhDetails, setTbhDetails] = useState(null);
  const [loadingTbh, setLoadingTbh] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [tableConfigState, setTableConfigState] = useState([]);
  const [toolbarConfigState, setToolbarConfigState] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [dataRefreshNonce, setDataRefreshNonce] = useState(0);

  const tableStateRef = useRef(null);
  const displayTableStateRef = useRef(null);
  const tokenVal = localStorage.getItem('authToken') || userProfile?.access_token || authToken || '';
  const isLoaded = Boolean(tokenVal || userProfile?.email);
  const tbhDetailsRef = useRef(null);
  const selectedTbhIdRef = useRef('');
  tbhDetailsRef.current = tbhDetails;
  const hasInitializedConfig = useRef(false);
  const hasInitializedToolbarConfig = useRef(false);

  const clearTbhSelection = useCallback(() => {
    selectedTbhIdRef.current = '';
    tbhDetailsRef.current = null;
    setTbhDetails(null);
    setLoadingTbh(false);
  }, []);

  useEffect(() => {
    const handleTableInteractions = (event) => {
      const target = event.target;
      if (!target?.closest) return;

      const isResetButton = Boolean(target.closest('.pi-refresh') || target.closest('button[aria-label*="Reset"]'));
      const isFilterReset = Boolean(target.closest('.tu-filter-footer-reset')
      || target.closest('button[aria-label*="Clear"]'));
      if (isResetButton || isFilterReset) {
        clearTbhSelection();
      }
      const isSortHeader = Boolean(target.closest('.table-only th.p-sortable-column'));
      setLoadingTbh(true);
      const isPaginationBtn = Boolean(
        target.closest('.table-only .p-paginator-element')
        || target.closest('.table-only .p-dropdown-item'),
      );

      if (isSortHeader || isPaginationBtn) {
        setTimeout(() => {
          const displayState = displayTableStateRef.current?.();
          const apiState = tableStateRef.current?.();
          if (apiState && apiState.fetchTableData && displayState) {
            // Extract the new page and limit directly from the display table's internal state
            const newPage = displayState.pagination?.page || 1;
            const newLimit = displayState.pagination?.limit || pagination.limit;

            apiState.fetchTableData(
              apiState.filters,
              { page: newPage, limit: newLimit },
              apiState.searchQuery,
            );
          }
        }, 100); // 100ms delay ensures the dynamic-table updates its internal state before we read it
      }
    };

    document.addEventListener('click', handleTableInteractions, true);
    return () => document.removeEventListener('click', handleTableInteractions, true);
  }, [clearTbhSelection, pagination.limit]);

  const handlePaginationChange = useCallback((newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
    setLoadingTbh(true);
    const apiTableState = tableStateRef.current?.();
    if (apiTableState && apiTableState.fetchTableData) {
      apiTableState.fetchTableData(
        apiTableState.filters,
        newPagination,
        apiTableState.searchQuery,
      );
    }
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    setLoadingTbh(true);
    if (!response) return response;
    try {
      const paginationData = response.meta || response.data?.meta || {};
      const safePage = Number(paginationData.page) || 1;
      const safeLimit = Number(paginationData.pageSize || paginationData.limit) || 10;
      const safeTotal = Number(paginationData.total) || 0;

      if (safePage === 1) {
        setDataRefreshNonce((prev) => prev + 1);
      }

      setPagination((prev) => ({
        ...prev,
        page: safePage,
        limit: safeLimit,
        total: safeTotal,
        totalPages: paginationData.totalPages,
      }));
      response.meta = {
        ...paginationData,
        page: safePage,
        limit: safeLimit,
        pageSize: safeLimit,
        total: safeTotal,
        totalRecords: safeTotal,
      };
      if (response.data) {
        response.data.meta = response.meta;
        response.data.pagination = response.meta;
      }
      if (response.success && response.data) {
        const normalizedResponse = normalizeTableUtilityResponse(response);
        const payload = normalizedResponse.data || {};
        if (payload.tableData) {
          setTableData(payload.tableData);
          if (payload.tableData.length > 0) {
            const uniqueTbhIds = [...new Set(payload.tableData.map((item) => item.tbh_id))];
            if (uniqueTbhIds.length === 1) {
              setTbhDetails(payload.tableData[0]);
            } else {
              setTbhDetails(null);
            }
          } else {
            setTbhDetails(null);
          }
        }
        if (payload.tableConfiguration && !hasInitializedConfig.current) {
          const enhancedConfig = payload.tableConfiguration.map((col) => {
            if (col?.menuItems) {
              return {
                ...col,
                menuItems: col?.menuItems?.map((menu) => ({
                  ...menu,
                  items: menu?.items?.map((item) => {
                    if (item.type === 'filter') return { ...item, selectedValues: [] };
                    return item;
                  }),
                })),
              };
            }
            return col;
          });
          setTableConfigState(enhancedConfig);
          hasInitializedConfig.current = true;
        }
        if (response.data.toolbarTableConfig && !hasInitializedToolbarConfig.current) {
          const tempResponse = { data: { tableConfiguration: response.data.toolbarTableConfig, tableData: [] } };
          const normalizedTemp = normalizeTableUtilityResponse(tempResponse);
          setToolbarConfigState(normalizedTemp.data.tableConfiguration);
          hasInitializedToolbarConfig.current = true;
        }
      }
    } catch (error) {
      throw new Error(`Error processing API response: ${error}`);
    }
    return response;
  }, []);

  const handleFilterUpdate = useCallback(({ action, filters }) => {
    if (action === 'reset') {
      selectedTbhIdRef.current = '';
      return;
    }

    if (action === 'apply') {
      selectedTbhIdRef.current = getSelectedTbhIdFromFilters(filters);
    }
  }, []);

  const paddedTableData = useMemo(() => {
    if (!pagination.total) return tableData;
    const arr = Array.from({ length: pagination.total }, (ignoredItem, index) => ({
      id: `pad-${index}`,
      tbh_id: '',
      project_name: '',
    }));
    const startIndex = (pagination.page - 1) * pagination.limit;
    tableData.forEach((item, index) => {
      if (startIndex + index < arr.length) {
        arr[startIndex + index] = item;
      }
    });
    return arr;
  }, [tableData, pagination]);

  const toolbarConfiguration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      showToolBar: true,
      showPagination: false,
      userInfo: {
        emailId: userProfile?.email || '',
        id: userProfile?.id || '',
        permissions: userProfile?.permissions || {},
        roleDetails: userProfile?.roleDetails || [],
        employeeId: userProfile?.employeeId || '',
      },
      headers: { token: tokenVal },
      additionalPayloadData: {
        get selectedTbhId() {
          return selectedTbhIdRef.current || tbhDetailsRef.current?.tbh_id || '';
        },
        get sort() {
          const displayState = displayTableStateRef.current?.();
          if (displayState && displayState.sortField) {
            return [{ field: displayState.sortField, order: displayState.sortOrder }];
          }
          return [];
        },
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => `TBH_ResourceManagement_${new Date().getTime()}`,
          sheetName: () => 'TBH ResourceManagement',
        },
      },
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: false,
        showDownloadCsv: true,
        showColumnFilterMenu: true,
        showFilterOption: true,
        showToolBarHeading: true,
        toolBarHeading: 'TBH Resource Management',
        downloadCsvFileName: 'TBH Resource Management',
        showSaveView: false,
        showSearch: false,
        showColumnCustomize: false,
        showHelp: false,
        showReset: false,
      },
      handler: {
        handleFilteredApiResponse,
        setTableStateHandler: (getState) => {
          tableStateRef.current = getState;
          setLoadingTbh(true);
        },
        onFilterUpdate: handleFilterUpdate,
      },
    },
    tableSettings: {
      size: 'small',
      tableConfig: toolbarConfigState,
      showExpandCollapse: false,
      loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
      pageSize: 10,
      paginationConfig: {
        page: 1,
        limit: 10,
        rows: 10,
        total: 0,
        rowsPerPageOptions: [10, 15, 25, 50],
      },
    },
  }), [
    userProfile,
    authToken,
    toolbarConfigState,
    tokenVal,
    handleFilteredApiResponse,
    handleFilterUpdate,
  ]);

  const tableConfigurationForUtility = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      baseURL: '',
      showToolBar: false,
      showPagination: true,
      userInfo: {
        emailId: userProfile?.email || '',
        id: userProfile?.id || '',
      },
      headers: {},
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: false },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: { endpoint: '', enabled: false },
      },
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: false,
        showColumnFilterMenu: false,
        showFilterOption: false,
        showToolBarHeading: false,
        toolBarHeading: '',
        showSaveView: false,
        showSearch: false,
        showColumnCustomize: false,
        showHelp: false,
        showReset: false,
      },
      handler: {
        setTableStateHandler: (getState) => {
          displayTableStateRef.current = getState;
        },
      },
    },
    tableSettings: {
      size: 'small',
      loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
      pageSize: pagination.limit,
      tableConfig: tableConfigState,
      tableScrollHeight: 'auto',
      paginationConfig: {
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        onPaginationChange: handlePaginationChange,
        rows: 10,
        rowsPerPageOptions: [10, 15, 25, 50],
      },
      showExpandCollapse: false,
    },
  }), [tableConfigState, userProfile, pagination, handlePaginationChange]);

  if (!isLoaded) return null;

  return (
    <div className="tbh-resource-mgmt-wrapper">
      <div className="tbh-resource-mgmt-table-utility table-utility toolbar-only">
        <TableUtility configuration={toolbarConfiguration} data={[]} />
      </div>
      {tbhDetails && (
        <TbhProfileHeader tbhData={tbhDetails} loading={loadingTbh} />
      )}
      <div className="tbh-resource-mgmt-table-utility table-utility table-only">
        <TableUtility
          key={`display-table-${dataRefreshNonce}`}
          configuration={tableConfigurationForUtility}
          data={paddedTableData}
        />
      </div>
    </div>
  );
};

export default TbhResourceManagement;
